import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { FactorControls } from "./FactorControls";

interface AsinDensityScoringCardProps {
  asinDensityMax: number;
  onAsinDensityMaxChange: (v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

const DENSITY_ROWS = [0, 25, 50, 75, 100];

export function AsinDensityScoringCard({
  asinDensityMax,
  onAsinDensityMaxChange,
  enabled,
  onEnabledChange,
}: AsinDensityScoringCardProps) {
  return (
    <Card id="kps-module-asin-density" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="ASIN Density Scoring"
            description="Percentage of top 50 sub-category ASINs ranking on this keyword, scaled linearly. Higher density = keyword is more broadly relevant to the category."
          />
          <FactorControls
            id="kps-toggle-asin-density"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={asinDensityMax}
            onValueChange={onAsinDensityMaxChange}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <Alert variant="default">
            <AlertDescription className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Formula</p>
              <p className="text-base tabular-nums text-foreground">
                <span className="text-muted-foreground">score = </span>
                <span className="font-semibold">(density% / 100)</span>
                <span className="text-muted-foreground"> × </span>
                <span className="font-semibold tabular-nums">{asinDensityMax}</span>
              </p>
            </AlertDescription>
          </Alert>

          <div className="space-y-0">
            <div className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground">% of Top 50 ASINs Ranking</span>
              <span className="text-xs font-semibold text-foreground text-center">Points Earned</span>
              <span className="text-xs font-semibold text-foreground text-right">Score</span>
            </div>
            {DENSITY_ROWS.map((density) => {
              const max = asinDensityMax;
              const score = (density / 100) * max;
              const pct = max > 0 ? (score / max) * 100 : 0;
              const formatted = Number.isInteger(score) ? score.toString() : score.toFixed(2).replace(/\.?0+$/, "");
              return (
                <div key={density} className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 py-3 border-b border-border last:border-b-0">
                  <span className="text-sm text-foreground tabular-nums">{density}%</span>
                  <Progress value={pct} className="h-2" indicatorClassName="bg-primary" />
                  <span className="text-sm tabular-nums text-right">
                    <span className="text-foreground">{formatted}</span>
                    <span className="text-xs text-muted-foreground"> / {max}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
